import { useCallback, useEffect, useMemo, useRef, useState, type BaseSyntheticEvent } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import {
  type TEntry,
  type TExhibitorWithEntries,
  type TPerson,
  type TShow,
  type UpdateShowExhibitorOutput,
} from '@schemas'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  useAllocateEntryCodes,
  useCreateEntry,
  useCheckInEntry,
  useDivisions,
  useExhibitors,
  useDeleteEntry,
  useMarkLabelPrinted,
  useUpdateShowExhibitor,
  useUpdateShowEntrantFinalization,
} from '../api/hooks'
import {
  useEntryIntakeManager,
  type PendingIntakeEntry,
  type CheckedInExhibit,
} from '@/hooks/useEntryIntake'
import { PENDING_SYNC_DEFERRED_MESSAGE, syncPendingEntry } from '@/hooks/pending-entry-sync'
import { apiClient } from '@/lib/apiClient'
import { usePendingEntrySync } from '@/hooks/usePendingEntrySync'
import { usePendingEntryAutoSync } from '@/hooks/usePendingEntryAutoSync'
import { useCurrentShow } from '@/context/current-show'
import { useRunMode, type UIMode } from '@/context/run-mode'
import { useLabelPrinter, useLabelPrinterDiscovery } from '@/context/label-printer'
import { SelectShowNotice } from '@/components/current-show'
import { toast } from 'sonner'
import { formatRoleList, useActorPermissions } from '@/hooks/useActorPermissions'
import { PageHeading } from '@/components/page-heading'
import { LabelPrinterToolbar } from '@/components/label-printer-toolbar'
import { LabelPrintingDisabledNotice } from '@/components/label-printing-disabled-notice'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { MergePeopleSheet } from '@/components/people/merge-people-sheet'
import { appConfig } from '@/lib/config'
import { showAllowsLabelPrinting } from '@/lib/label/permissions'
import { normalizeOptional } from '@/features/intake/utils'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { parseApiErrorMessage, parseApiErrorPayload, parseApiErrorStatus } from '@/lib/api-errors'
import { EntryEligibilityError, staffEntryRestriction } from '@/lib/entry-eligibility'
import { useDeleteEntryAction } from '@/hooks/useDeleteEntryAction'
import { EntryFormCard } from './checkin/EntryFormCard'
import { CheckInStepper } from './checkin/CheckInStepper'
import { EntrantStep, type EntrantReadyContext } from './checkin/steps/EntrantStep'
import { ClubStep } from './checkin/steps/ClubStep'
import { EntriesStep } from './checkin/steps/EntriesStep'
import { FinalizeStep } from './checkin/steps/FinalizeStep'
import {
  entryFormSchema,
  type DivisionOption,
  type EnteredClass,
  type EntryFormValues,
  type ExhibitorEntrySummary,
} from './checkin/types'
import type { SelectedExhibitorEntriesListProps } from '@/components/entries/SelectedExhibitorEntriesList'
import { RecentCheckIns } from '@/components/entries/RecentCheckIns'
import { entryInCompetition } from '@schemas'
import {
  buildShowEntrantClubUpdatePayload,
  mergeShowEntrantIntoExhibitor,
  normalizeShowEntrantClubSelection,
  updateExhibitorCacheAfterShowEntrant,
} from '@/features/entrants/showEntrantClub'

// The server refused an entry the picker could not know was a duplicate: another device saved
// one, or an offline entry synced late.
type DuplicateConflictState = {
  submissionId?: string
  className: string | null
  message: string
}

const isOfflineError = (error: unknown) => {
  if (!(error instanceof Error)) return false
  const message = error.message.toLowerCase()
  return (
    message.includes('unable to reach api') ||
    message.includes('fetch failed') ||
    message.includes('network request failed')
  )
}

function useSafeQueryClient() {
  try {
    return useQueryClient()
  } catch {
    return null
  }
}

type ClubSelection = { clubId: string | null; clubName: string }

// Returns the same object when it is already empty so clearing does not re-render.
const clearClubSelection = (current: ClubSelection): ClubSelection =>
  current.clubId === null && current.clubName === '' ? current : { clubId: null, clubName: '' }

export default function CheckInPage() {
  const { currentShow, clearCurrentShow } = useCurrentShow()
  const { mode } = useRunMode()

  if (!currentShow) {
    if (mode === 'run') {
      return (
        <SelectShowNotice
          title="Select a show to intake entries"
          description="Pick a show and move it to Accepting entries before using Run mode features."
          actionLabel="Choose show"
        />
      )
    }
    return <SelectShowNotice description="Choose a show to intake entries." />
  }

  return (
    <CheckInPageWithShow
      key={currentShow.id}
      currentShow={currentShow}
      clearCurrentShow={clearCurrentShow}
      mode={mode}
    />
  )
}

function CheckInPageWithShow({
  currentShow,
  clearCurrentShow,
  mode,
}: {
  currentShow: TShow
  clearCurrentShow: () => void
  mode: UIMode
}) {
  const showId = currentShow.id ?? null
  const labelPrintingDisabled = currentShow.labelPrintingDisabled ?? false
  const allowsLabelPrinting = showAllowsLabelPrinting(currentShow)
  useLabelPrinterDiscovery(allowsLabelPrinting)
  const intake = useEntryIntakeManager(showId ?? undefined)
  const allocateCodes = useAllocateEntryCodes()
  const createEntry = useCreateEntry()
  const checkInEntry = useCheckInEntry()
  const markLabelPrinted = useMarkLabelPrinted()
  const deleteEntry = useDeleteEntry()
  const labelPrinter = useLabelPrinter()
  const { strategy: labelStrategy, selectedPrinter, printEntryLabel: sendToPrinter } = labelPrinter
  const permissions = useActorPermissions(showId ?? undefined)
  const queryClient = useSafeQueryClient()
  const updateShowExhibitor = useUpdateShowExhibitor()
  const updateShowEntrantFinalization = useUpdateShowEntrantFinalization()
  const [selectedExhibitor, setSelectedExhibitor] = useState<TExhibitorWithEntries | null>(null)
  const selectedExhibitorRef = useRef<TExhibitorWithEntries | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeTarget, setMergeTarget] = useState<Pick<TPerson, 'id' | 'name'> | null>(null)
  const autoFetchLockRef = useRef(false)
  const previousStatusRef = useRef<string | null>(null)
  const preselectHandledRef = useRef(false)
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  )
  const [autoPrintEntryCode, setAutoPrintEntryCode] = useState<string | null>(null)
  const [autoPrintError, setAutoPrintError] = useState<{
    entry: ExhibitorEntrySummary
    checkedIn: CheckedInExhibit
    message: string
  } | null>(null)
  const [autoPrintInFlight, setAutoPrintInFlight] = useState(false)
  const [duplicateConflict, setDuplicateConflict] = useState<DuplicateConflictState | null>(null)
  const [highlightEntryId, setHighlightEntryId] = useState<string | null>(null)
  const highlightTimeoutRef = useRef<number | null>(null)
  const clubsEnabled = currentShow.enableClubs !== false
  const [pendingStepId, setPendingStepId] = useState<string | undefined>(undefined)
  const [clubStepEnabled, setClubStepEnabled] = useState(false)
  const [entrantGrandChampionEligible, setEntrantGrandChampionEligible] = useState(true)
  const pendingStepIdRef = useRef<string | undefined>(undefined)
  const lastStepIdRef = useRef<string | null>(null)
  const [entrantClubSelection, setEntrantClubSelection] = useState<{
    clubId: string | null
    clubName: string
  }>({ clubId: null, clubName: '' })
  const [clubSelectionDirty, setClubSelectionDirty] = useState(false)
  const clubSelectionSource = useRef({ context: '', clubId: null as string | null, clubName: '' })
  const [clubSavePending, setClubSavePending] = useState(false)
  const hideEntryCodes = appConfig.hideEntryCodeForJudges && mode === 'run'
  const location = useLocation()
  const navigate = useNavigate()
  const divisionsQuery = useDivisions(showId ?? undefined)
  const exhibitorsQuery = useExhibitors(
    {
      showId: showId ?? undefined,
      limit: 500,
    },
    { enabled: Boolean(showId) }
  )

  useEffect(() => {
    if (createEntry.isPending || checkInEntry.isPending || updateShowExhibitor.isPending) return
    setSelectedExhibitor(previous => {
      if (!previous) return previous
      return exhibitorsQuery.data?.find(entrant => entrant.id === previous.id) ?? previous
    })
  }, [
    exhibitorsQuery.data,
    createEntry.isPending,
    checkInEntry.isPending,
    updateShowExhibitor.isPending,
  ])

  const entryForm = useForm<EntryFormValues>({
    resolver: zodResolver(entryFormSchema),
    defaultValues: {
      divisionId: '',
      classId: '',
      notes: '',
    },
  })

  const exhibitorsByPersonId = useMemo(() => {
    const map = new Map<string, TExhibitorWithEntries>()
    for (const exhibitor of exhibitorsQuery.data ?? []) {
      const key = exhibitor.personId ?? exhibitor.person?.id
      if (key) {
        map.set(key, exhibitor)
      }
    }
    return map
  }, [exhibitorsQuery.data])

  const resolveExhibitorClubDefaults = useCallback(
    (entrant: TExhibitorWithEntries | null): { clubId: string | null; clubName: string } => {
      if (!entrant || !clubsEnabled) {
        return { clubId: null, clubName: '' }
      }

      const personKey = entrant.personId ?? entrant.person?.id ?? null
      const enriched = personKey ? (exhibitorsByPersonId.get(personKey) ?? entrant) : entrant

      const showClubId =
        'showClubId' in enriched &&
        typeof (enriched as { showClubId?: unknown }).showClubId === 'string'
          ? ((enriched as { showClubId?: string | null }).showClubId ?? null)
          : null
      const showClubName =
        'showClubName' in enriched &&
        typeof (enriched as { showClubName?: unknown }).showClubName === 'string'
          ? ((enriched as { showClubName?: string | null }).showClubName ?? null)
          : null

      if (showClubId) {
        return { clubId: showClubId, clubName: showClubName ?? '' }
      }

      if (showClubName) {
        return { clubId: null, clubName: showClubName }
      }

      const overridePresence =
        ('showClubId' in enriched && enriched.showClubId !== undefined) ||
        ('showClubName' in enriched && enriched.showClubName !== undefined)

      const lastClubId =
        'lastKnownClubId' in enriched &&
        typeof (enriched as { lastKnownClubId?: unknown }).lastKnownClubId === 'string'
          ? ((enriched as { lastKnownClubId?: string | null }).lastKnownClubId ?? null)
          : null
      const lastClubName =
        'lastKnownClubName' in enriched &&
        typeof (enriched as { lastKnownClubName?: unknown }).lastKnownClubName === 'string'
          ? ((enriched as { lastKnownClubName?: string | null }).lastKnownClubName ?? null)
          : null

      if (lastClubId) {
        return { clubId: lastClubId, clubName: lastClubName ?? '' }
      }

      const fallbackAffiliation =
        'affiliation' in enriched &&
        typeof (enriched as { affiliation?: unknown }).affiliation === 'string'
          ? ((enriched as { affiliation?: string | null }).affiliation ?? '')
          : ''

      if (overridePresence) {
        return { clubId: showClubId ?? null, clubName: showClubName ?? '' }
      }

      return { clubId: null, clubName: lastClubName ?? fallbackAffiliation ?? '' }
    },
    [clubsEnabled, exhibitorsByPersonId]
  )

  const applyExhibitorClubDefaults = useCallback(
    (next: TExhibitorWithEntries | null) => {
      if (!clubsEnabled) {
        setEntrantClubSelection(clearClubSelection)
        setClubSelectionDirty(false)
        return
      }

      const defaults = resolveExhibitorClubDefaults(next)
      setEntrantClubSelection(current => {
        if (current.clubId === defaults.clubId && current.clubName === defaults.clubName) {
          return current
        }
        return defaults
      })
      setClubSelectionDirty(false)
    },
    [clubsEnabled, resolveExhibitorClubDefaults]
  )

  useEffect(() => {
    selectedExhibitorRef.current = selectedExhibitor
    const context = `${showId}:${selectedExhibitor?.id ?? ''}:${clubsEnabled}`
    const previous = clubSelectionSource.current
    const defaults = resolveExhibitorClubDefaults(selectedExhibitor)
    const changedClub =
      previous.clubId !== defaults.clubId || previous.clubName !== defaults.clubName
    clubSelectionSource.current = { context, ...defaults }
    if (previous.context !== context || (changedClub && !clubSelectionDirty)) {
      applyExhibitorClubDefaults(selectedExhibitor)
    }
  }, [
    applyExhibitorClubDefaults,
    resolveExhibitorClubDefaults,
    selectedExhibitor,
    showId,
    clubsEnabled,
    clubSelectionDirty,
  ])

  useEffect(() => {
    const nextEligible = selectedExhibitor?.grandChampionEligible !== false
    setEntrantGrandChampionEligible(nextEligible)
  }, [selectedExhibitor?.grandChampionEligible, selectedExhibitor?.id])

  useEffect(() => {
    pendingStepIdRef.current = pendingStepId
  }, [pendingStepId])

  useEffect(() => {
    if (!clubsEnabled) {
      setEntrantClubSelection(clearClubSelection)
      setClubStepEnabled(false)
      return
    }
    if (!selectedExhibitor) {
      setClubStepEnabled(false)
    }
  }, [clubsEnabled, selectedExhibitor])

  const divisionOptions: DivisionOption[] = useMemo(() => {
    if (!divisionsQuery.data) return []
    return divisionsQuery.data.map(division => ({
      id: division.id ?? division.code,
      name: division.name,
      classes: division.classes ?? [],
    }))
  }, [divisionsQuery.data])

  const selectedDivisionId = entryForm.watch('divisionId')
  const selectedClassId = entryForm.watch('classId')

  const classLookup = useMemo(() => {
    const map = new Map<string, { code: string | null; labelsPerExhibit: number }>()
    const divisions = divisionsQuery.data ?? []
    for (const division of divisions) {
      const classes = Array.isArray(division.classes) ? division.classes : []
      for (const klass of classes) {
        const id = klass?.id ?? null
        if (typeof id === 'string' && id.length > 0) {
          map.set(id, {
            code: typeof klass.code === 'string' ? klass.code : null,
            labelsPerExhibit: resolveLabelsPerExhibitFromClass(klass),
          })
        }
      }
    }
    return map
  }, [divisionsQuery.data])

  const classOptions = useMemo(() => {
    if (!selectedDivisionId) return []
    const division = divisionOptions.find(item => item.id === selectedDivisionId)
    return division?.classes ?? []
  }, [divisionOptions, selectedDivisionId])

  // A person keeps one active entry per class, across every entrant record linked to them (a
  // merge can leave two). Saved entries hold their class unless marked Not Entered, and so does
  // anything this device has queued for those records.
  const enteredClasses = useMemo(() => {
    const entered = new Map<string, EnteredClass>()
    if (!selectedExhibitor) return entered
    const personId = selectedExhibitor.personId ?? selectedExhibitor.person?.id
    const records = [
      selectedExhibitor,
      ...(personId
        ? (exhibitorsQuery.data ?? []).filter(
            exhibitor =>
              exhibitor.id !== selectedExhibitor.id &&
              (exhibitor.personId ?? exhibitor.person?.id) === personId
          )
        : []),
    ]
    for (const entry of records.flatMap(record => record.entries ?? [])) {
      const classId = entry?.class?.id
      if (classId && entryInCompetition(entry.status) && !entered.has(classId))
        entered.set(classId, { entryCode: entry.entryCode, pending: false })
    }
    const recordIds = new Set(records.map(record => record.id))
    for (const pending of intake.pendingEntries) {
      if (recordIds.has(pending.exhibitor.id) && !entered.has(pending.classId))
        entered.set(pending.classId, { entryCode: pending.code, pending: true })
    }
    return entered
  }, [exhibitorsQuery.data, intake.pendingEntries, selectedExhibitor])

  const fullyEnteredDivisionIds = useMemo(
    () =>
      new Set(
        divisionOptions
          .filter(
            division =>
              division.classes.length > 0 &&
              division.classes.every(klass => klass.id && enteredClasses.has(klass.id))
          )
          .map(division => division.id)
      ),
    [divisionOptions, enteredClasses]
  )

  const getLabelsPerEntry = useCallback(
    (entry: ExhibitorEntrySummary) => {
      const classId = entry.class?.id ?? null
      if (classId && classLookup.has(classId)) {
        return classLookup.get(classId)?.labelsPerExhibit ?? 1
      }
      return 1
    },
    [classLookup]
  )

  const hasDivisions = divisionOptions.length > 0
  const hasClasses = classOptions.length > 0
  const allClassesEntered = hasClasses && fullyEnteredDivisionIds.has(selectedDivisionId)

  useEffect(() => {
    if (!highlightEntryId) return
    const entries = selectedExhibitor?.entries ?? []
    if (!entries.some(entry => entry?.id === highlightEntryId)) {
      setHighlightEntryId(null)
    }
  }, [highlightEntryId, selectedExhibitor])

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (preselectHandledRef.current) return
    const state = location.state as {
      exhibitorId?: string
      personId?: string
      exhibitor?: TExhibitorWithEntries
    } | null
    if (!state) return
    if (!showId) return

    let match: TExhibitorWithEntries | undefined = state.exhibitor ?? undefined

    if (!match && state.exhibitorId && exhibitorsQuery.data) {
      match = exhibitorsQuery.data.find(exhibitor => exhibitor.id === state.exhibitorId)
    }

    if (!match && state.personId) {
      match = exhibitorsByPersonId.get(state.personId)
    }

    if (!match) return

    preselectHandledRef.current = true
    setSelectedExhibitor(match)
    navigate(location.pathname, { replace: true })
  }, [
    showId,
    exhibitorsByPersonId,
    exhibitorsQuery.data,
    location.pathname,
    location.state,
    navigate,
  ])

  useEffect(() => {
    if (!hasDivisions) {
      entryForm.setValue('divisionId', '')
      return
    }
    const currentDivision = entryForm.getValues('divisionId')
    if (!currentDivision || !divisionOptions.some(division => division.id === currentDivision)) {
      entryForm.setValue('divisionId', divisionOptions[0].id)
    }
  }, [divisionOptions, entryForm, hasDivisions])

  useEffect(() => {
    if (!hasClasses) {
      entryForm.setValue('classId', '')
      return
    }
    const position = classOptions.findIndex(cls => cls.id === selectedClassId)
    if (position >= 0 && !enteredClasses.has(selectedClassId)) return
    // Move on to the next class the entrant has not entered, in schedule order.
    const next = [...classOptions.slice(position + 1), ...classOptions.slice(0, position + 1)].find(
      cls => cls.id && !enteredClasses.has(cls.id)
    )
    if ((next?.id ?? '') !== selectedClassId) entryForm.setValue('classId', next?.id ?? '')
  }, [classOptions, enteredClasses, entryForm, hasClasses, selectedClassId])

  useEffect(() => {
    setDuplicateConflict(null)
  }, [selectedExhibitor?.id, showId])

  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const canManageExhibitors = permissions.can('exhibitors:write')
  const canManageEntries = permissions.can('entries:write')
  const canAllocateCodes = permissions.can('entrycodes:allocate')
  const canDownloadEntrantPacket = permissions.can('reports:entrant_packet')
  const canMergePeople = permissions.can('people:merge')

  const exhibitorRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('exhibitors:write')),
    [permissions]
  )
  const entriesRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entries:write')),
    [permissions]
  )
  const allocateRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entrycodes:allocate')),
    [permissions]
  )
  const packetRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('reports:entrant_packet')),
    [permissions]
  )
  const mergeRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('people:merge')),
    [permissions]
  )

  const exhibitorPermissionDisabled = permissionLoading || !canManageExhibitors
  const exhibitorPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canManageExhibitors
      ? `Creating exhibitors requires ${exhibitorRolesLabel}.`
      : undefined
  const grandChampionToggleDisabled =
    exhibitorPermissionDisabled || updateShowExhibitor.isPending || !showId || !isOnline
  const grandChampionToggleTitle = !showId
    ? 'Select a show before updating eligibility.'
    : !isOnline
      ? 'Connect to the network before updating eligibility.'
      : exhibitorPermissionReason
  const entryPermissionDisabled = permissionLoading || !canManageEntries
  const entryPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canManageEntries
      ? `Creating entries requires ${entriesRolesLabel}.`
      : undefined
  const staffMayExhibit = currentShow.staffMayExhibit !== false
  const selectedEntrantIsStaff = selectedExhibitor?.id
    ? intake.assignmentContexts?.[selectedExhibitor.id]?.isShowStaff
    : undefined
  // Online submissions verify unknown eligibility automatically before queuing.
  const selectedEntryRestriction =
    selectedExhibitor && (!isOnline || selectedEntrantIsStaff !== undefined)
      ? staffEntryRestriction(staffMayExhibit, selectedEntrantIsStaff)
      : undefined
  const selectedEntryDisabled = entryPermissionDisabled || Boolean(selectedEntryRestriction)
  const selectedEntryReason = entryPermissionReason ?? selectedEntryRestriction

  // Intake never waits on a stalled network: a refresh that has not replied in
  // time resolves to nothing and leaves the last snapshot in use.
  const refreshAssignmentContextBriefly = useCallback(
    async (exhibitorId: string) => {
      let timer: number | undefined
      try {
        return await Promise.race([
          intake.refreshAssignmentContext(exhibitorId),
          new Promise<undefined>(resolve => {
            timer = window.setTimeout(resolve, 1500)
          }),
        ])
      } finally {
        window.clearTimeout(timer)
      }
    },
    [intake.refreshAssignmentContext]
  )

  const verifyEntrantEntryEligibility = useCallback(
    async (exhibitorId: string) => {
      if (staffMayExhibit) return true
      // Without a timely reply the offline rule applies: only a non-staff result
      // already verified on this device lets intake continue.
      let isShowStaff = intake.assignmentContexts?.[exhibitorId]?.isShowStaff
      if (isOnline) {
        try {
          const fresh = await refreshAssignmentContextBriefly(exhibitorId)
          if (fresh) isShowStaff = fresh.isShowStaff
        } catch (error) {
          const status = parseApiErrorStatus(error)
          if (status !== null && status < 500) {
            // The server refused the read; that is not a connection problem.
            toast.error(
              `Unable to verify staff eligibility. ${parseApiErrorMessage(error, 'Try again.')}`
            )
            return false
          }
        }
      }
      const restriction = staffEntryRestriction(false, isShowStaff)
      if (restriction) toast.error(restriction)
      return !restriction
    },
    [staffMayExhibit, isOnline, refreshAssignmentContextBriefly, intake.assignmentContexts]
  )
  const allocationPermissionDisabled = permissionLoading || !canAllocateCodes
  const allocationPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canAllocateCodes
      ? `Fetching Exhibit IDs requires ${allocateRolesLabel}.`
      : undefined
  const packetPermissionDisabled = permissionLoading || !canDownloadEntrantPacket
  const packetPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canDownloadEntrantPacket
      ? `Downloading entrant packets requires ${packetRolesLabel}.`
      : undefined
  const mergePermissionDisabled = permissionLoading || !canMergePeople
  const mergePermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canMergePeople
      ? `Merging people requires ${mergeRolesLabel}.`
      : undefined
  const allClassesEnteredMessage =
    selectedExhibitor && allClassesEntered
      ? `${selectedExhibitor.name ?? 'This entrant'} has entered every class in ${
          divisionOptions.find(division => division.id === selectedDivisionId)?.name ??
          'this division'
        }.`
      : undefined
  const entryButtonDisabled =
    !selectedExhibitor ||
    entryForm.formState.isSubmitting ||
    createEntry.isPending ||
    !hasDivisions ||
    !hasClasses ||
    allClassesEntered ||
    selectedEntryDisabled
  const entryButtonTooltip = selectedEntryReason ?? allClassesEnteredMessage

  const printerConfigured = labelStrategy === 'browser' || Boolean(selectedPrinter)
  const autoPrintReady = allowsLabelPrinting && isOnline && printerConfigured
  const autoPrintStatusMessage = !allowsLabelPrinting
    ? 'Label printing is disabled for this show. Create Exhibit ID labels manually.'
    : !printerConfigured
      ? labelStrategy === 'dymo'
        ? 'Select a DYMO printer to auto-print Exhibit IDs after check-in.'
        : 'Set up label printing to auto-print Exhibit IDs after check-in.'
      : !isOnline
        ? 'Automatic printing is skipped while offline.'
        : undefined
  const showPrinterSetupHint = allowsLabelPrinting && !printerConfigured
  const entryAutoPrintStatusMessage = showPrinterSetupHint ? undefined : autoPrintStatusMessage

  const basePrimaryLabel = autoPrintReady ? 'Create, Check In, & Print' : 'Create & Check In'

  const primarySubmitLabel = createEntry.isPending
    ? 'Creating entry…'
    : entryPermissionDisabled
      ? 'Permission required'
      : !hasDivisions || !hasClasses
        ? 'Add classes in Setup first'
        : selectedExhibitor
          ? basePrimaryLabel
          : 'Select an exhibitor first'

  const entryActionMessage = !selectedExhibitor
    ? 'Select an exhibitor to continue.'
    : !hasDivisions || !hasClasses
      ? 'Add at least one division and class during setup before intake.'
      : entryPermissionDisabled
        ? entryPermissionReason
        : (selectedEntryRestriction ?? allClassesEnteredMessage)

  useEffect(() => {
    if (!showId) {
      setSelectedExhibitor(null)
      setHighlightEntryId(null)
      return
    }
  }, [showId])

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  const handledStaleShowRef = useRef(false)

  useEffect(() => {
    const errors = [divisionsQuery.error, exhibitorsQuery.error]
    const notFound = errors.find(error => error instanceof Error && error.message.includes('[404]'))

    if (!notFound || handledStaleShowRef.current) return

    handledStaleShowRef.current = true
    toast.error('The selected show could not be found. Please choose another show.')
    clearCurrentShow()
    setSelectedExhibitor(null)
    setHighlightEntryId(null)
    autoFetchLockRef.current = false
  }, [clearCurrentShow, divisionsQuery.error, exhibitorsQuery.error, setSelectedExhibitor])

  useEffect(() => {
    if (currentShow) {
      handledStaleShowRef.current = false
    }
  }, [currentShow])

  useEffect(() => {
    const previousStatus = previousStatusRef.current
    const currentStatus = currentShow.status ?? null
    if (previousStatus === 'accepting_entries' && currentStatus !== 'accepting_entries') {
      toast.error('This show is no longer accepting entries. Intake actions are disabled.')
      autoFetchLockRef.current = false
    }
    previousStatusRef.current = currentStatus
  }, [currentShow.status])

  const allocateAndStoreCodes = useCallback(
    async (
      targetShowId: string | null,
      count: number,
      options?: { silent?: boolean }
    ): Promise<number> => {
      if (!targetShowId) {
        toast.error('Select a show before fetching Exhibit IDs.')
        return 0
      }
      if (allocationPermissionDisabled) {
        toast.error(
          allocationPermissionReason ?? 'Fetching Exhibit IDs requires additional permissions.'
        )
        return 0
      }
      if (!isOnline) {
        toast.error('Cannot fetch Exhibit IDs while offline')
        return 0
      }
      try {
        const { codes, identifiers } = await allocateCodes.mutateAsync({
          showId: targetShowId,
          count,
        })
        await intake.pushCodes(codes, identifiers)
        if (!options?.silent) {
          toast.success(`Fetched ${codes.length} Exhibit IDs`)
        }
        return codes.length
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        } else {
          toast.error('Unable to allocate Exhibit IDs')
        }
        return 0
      }
    },
    [
      allocationPermissionDisabled,
      allocationPermissionReason,
      allocateCodes,
      intake.pushCodes,
      isOnline,
    ]
  )

  useEffect(() => {
    if (currentShow.status !== 'accepting_entries') {
      autoFetchLockRef.current = false
      return
    }
    if (!showId) return
    if (!intake.initialized) return
    if (!hasDivisions) return
    if (!isOnline) return
    if (allocateCodes.isPending) return
    if (allocationPermissionDisabled) {
      autoFetchLockRef.current = false
      return
    }
    if (intake.availableCodes.length >= 3) {
      autoFetchLockRef.current = false
      return
    }
    if (autoFetchLockRef.current) return
    autoFetchLockRef.current = true
    void allocateAndStoreCodes(showId, 10).finally(() => {
      autoFetchLockRef.current = false
    })
  }, [
    allocateCodes.isPending,
    allocateAndStoreCodes,
    allocationPermissionDisabled,
    showId,
    currentShow.status,
    hasDivisions,
    intake.availableCodes.length,
    intake.initialized,
    isOnline,
  ])

  const handleOpenMergePerson = (person: Pick<TPerson, 'id' | 'name'>) => {
    if (!person.id) {
      toast.error('Cannot merge a person without an id')
      return
    }
    if (mergePermissionDisabled) {
      toast.error(mergePermissionReason ?? 'Merging people is not available for this role.')
      return
    }
    setMergeTarget(person)
    setMergeOpen(true)
  }

  const handleMergeOpenChange = (open: boolean) => {
    setMergeOpen(open)
    if (!open) {
      setMergeTarget(null)
    }
  }

  const applyEntryUpdate = async (exhibitorId: string, entry: ExhibitorEntrySummary) => {
    // A list read may already hold this entry. Blanks in the local summary must not erase it.
    const known = Object.fromEntries(
      Object.entries(entry).filter(([, value]) => value != null)
    ) as Partial<ExhibitorEntrySummary>
    const update = (exhibitor: TExhibitorWithEntries): TExhibitorWithEntries => {
      if (exhibitor.id !== exhibitorId) return exhibitor
      const entries = exhibitor.entries ?? []
      return {
        ...exhibitor,
        entries: entries.some(existing => existing.id === entry.id)
          ? entries.map(existing =>
              existing.id === entry.id
                ? { ...existing, ...known, class: existing.class ?? entry.class }
                : existing
            )
          : [entry, ...entries],
      }
    }
    if (queryClient && showId) {
      const filters = { queryKey: ['exhibitors', { showId }] }
      // An in-flight list read may still contain the pre-write entry state.
      await queryClient.cancelQueries(filters)
      queryClient.setQueriesData<TExhibitorWithEntries[]>(filters, current => current?.map(update))
    }
    setSelectedExhibitor(previous => (previous ? update(previous) : previous))
  }

  const handleEntrySuccess = async (
    entry: TEntry,
    usedCode: string,
    originalCode?: string,
    options?: {
      className?: string | null
      classId?: string
      classCode?: string
      checkedIn?: CheckedInExhibit
    }
  ) => {
    await intake.clearLastClaimed()
    const exhibitorId = entry.exhibitorId ?? selectedExhibitor?.id
    const canonicalCode = entry.entryCode ?? usedCode
    const originalPersisted =
      originalCode && originalCode !== canonicalCode
        ? originalCode
        : usedCode.startsWith('TMP') && usedCode !== canonicalCode
          ? usedCode
          : undefined
    const recentClassName = options?.className ?? null
    const derivedClass = options?.className
      ? {
          id: options.classId ?? entry.classId ?? '',
          code: options.classCode ?? '',
          name: options.className,
        }
      : undefined

    if (exhibitorId) {
      await intake.recordRecentEntry({
        id: entry.id ?? `${exhibitorId}:${canonicalCode}`,
        entryCode: canonicalCode,
        exhibitorId,
        className: recentClassName,
        originalCode: originalPersisted ?? null,
      })
    }

    if (exhibitorId) {
      await applyEntryUpdate(exhibitorId, {
        id: entry.id ?? '',
        entryCode: entry.entryCode,
        status: options?.checkedIn ? 'checked_in' : (entry.status ?? 'draft'),
        exhibitId: options?.checkedIn?.exhibitId ?? null,
        title: entry.title ?? undefined,
        overrideDuplicateReason: entry.overrideDuplicateReason ?? null,
        overrideDuplicateBy: entry.overrideDuplicateBy ?? null,
        overrideDuplicateAt: entry.overrideDuplicateAt ?? null,
        class: derivedClass,
      })
    }
    const mapping =
      originalPersisted && originalPersisted !== canonicalCode
        ? `${originalPersisted} → ${canonicalCode}`
        : null
    toast.success(
      mapping ? `Entry ${canonicalCode} created (${mapping})` : `Entry ${canonicalCode} created`
    )
  }

  const { handleDeleteEntry, deleteEntryPendingId, canDeleteEntries, deleteDisabledReason } =
    useDeleteEntryAction({
      selectedExhibitor,
      setSelectedExhibitor,
      currentShowId: showId,
      deleteEntry,
      intake,
      queryClient,
    })

  const { syncPendingEntries } = usePendingEntrySync({
    intake,
    currentShowId: showId,
    currentShowStatus: currentShow.status ?? null,
    isOnline,
    entryPermissionDisabled,
    entryPermissionReason,
    allocationPermissionDisabled,
    allocationPermissionReason,
    createEntry,
    allocateCodes,
    onEntryCreated: async (entry, { usedCode, originalCode, className, classId }) => {
      await handleEntrySuccess(entry, usedCode, originalCode ?? undefined, {
        className,
        classId,
      })
    },
  })

  const handleSelectExhibitor = (exhibitor: TExhibitorWithEntries) => {
    setSelectedExhibitor(exhibitor)
    applyExhibitorClubDefaults(exhibitor)
    setClubSelectionDirty(false)
    setEntrantGrandChampionEligible(exhibitor.grandChampionEligible !== false)
    setHighlightEntryId(current => {
      if (!current) return null
      const entries = exhibitor.entries ?? []
      return entries.some(entry => entry?.id === current) ? current : null
    })
  }

  const handleEntrantReadyStep = useCallback(
    (_entrant: TExhibitorWithEntries, context: EntrantReadyContext) => {
      const shouldShowClubStep = clubsEnabled && context.source === 'existing-person'
      setClubStepEnabled(shouldShowClubStep)
      setPendingStepId(shouldShowClubStep ? 'club' : 'entries')
    },
    [clubsEnabled]
  )

  const handleClubSelectionChange = useCallback(
    (next: Partial<{ clubId: string | null; clubName: string }>) => {
      // State updater callbacks may run after this handler returns. Record the
      // user's edit synchronously so a concurrent read cannot reset the draft.
      setClubSelectionDirty(true)
      setEntrantClubSelection(previous => {
        const nextClubId =
          next.clubId !== undefined ? (next.clubId ?? null) : (previous.clubId ?? null)
        const nextClubName =
          next.clubName !== undefined ? (next.clubName ?? '') : (previous.clubName ?? '')

        if (previous.clubId === nextClubId && previous.clubName === nextClubName) {
          return previous
        }
        return { clubId: nextClubId, clubName: nextClubName }
      })
    },
    []
  )

  const applyShowEntrantUpdate = useCallback(
    (
      targetExhibitorId: string,
      showEntrant: UpdateShowExhibitorOutput['showEntrant']
    ): TExhibitorWithEntries | null => {
      let updatedEntrant: TExhibitorWithEntries | null = null
      setSelectedExhibitor(previous => {
        if (!previous || previous.id !== targetExhibitorId) return previous
        const next = mergeShowEntrantIntoExhibitor(previous, showEntrant)
        updatedEntrant = next
        return next
      })

      if (selectedExhibitorRef.current?.id === targetExhibitorId && updatedEntrant) {
        selectedExhibitorRef.current = updatedEntrant
      }

      updateExhibitorCacheAfterShowEntrant({
        queryClient,
        showId,
        exhibitorId: targetExhibitorId,
        showEntrant,
      })

      return updatedEntrant
    },
    [queryClient, showId]
  )

  const handleGrandChampionEligibilityChange = useCallback(
    async (nextEligible: boolean, options?: { exhibitorId?: string }) => {
      const targetExhibitorId = options?.exhibitorId ?? selectedExhibitor?.id ?? null
      const previousEligible = entrantGrandChampionEligible
      setEntrantGrandChampionEligible(nextEligible)

      if (!showId || !targetExhibitorId) {
        return
      }

      try {
        const result = await updateShowExhibitor.mutateAsync({
          showId,
          exhibitorId: targetExhibitorId,
          payload: { grandChampionEligible: nextEligible },
        })

        applyShowEntrantUpdate(targetExhibitorId, result)
      } catch (error) {
        setEntrantGrandChampionEligible(previousEligible)
        const message = parseApiErrorMessage(error)
        toast.error(message)
      }
    },
    [
      applyShowEntrantUpdate,
      showId,
      entrantGrandChampionEligible,
      parseApiErrorMessage,
      selectedExhibitor?.id,
      updateShowExhibitor,
    ]
  )

  const persistClubSelection = useCallback(
    async (options?: { silent?: boolean }): Promise<boolean> => {
      if (!clubsEnabled) return true
      if (!showId) return true

      const currentEntrant = selectedExhibitorRef.current
      if (!currentEntrant) return true
      if (!currentEntrant.id) return true
      if (!isOnline) {
        return true
      }

      const nextSelection = normalizeShowEntrantClubSelection({
        clubId: entrantClubSelection.clubId ?? null,
        clubName: entrantClubSelection.clubName,
      })
      const currentSelection = normalizeShowEntrantClubSelection({
        clubId: currentEntrant.showClubId ?? null,
        clubName: currentEntrant.showClubName ?? null,
      })

      const payload = buildShowEntrantClubUpdatePayload({
        next: nextSelection,
        current: currentSelection,
      })

      if (!payload) {
        setClubSelectionDirty(false)
        setEntrantClubSelection(previous => {
          const normalizedName = nextSelection.clubName ?? ''
          if (previous.clubId === nextSelection.clubId && previous.clubName === normalizedName) {
            return previous
          }
          return { clubId: nextSelection.clubId, clubName: normalizedName }
        })
        return true
      }

      try {
        setClubSavePending(true)
        const showEntrant = await updateShowExhibitor.mutateAsync({
          showId: showId,
          exhibitorId: currentEntrant.id,
          payload,
        })
        applyShowEntrantUpdate(currentEntrant.id, showEntrant)
        setEntrantClubSelection({
          clubId: showEntrant.clubId ?? null,
          clubName: showEntrant.clubName ?? '',
        })
        setClubSelectionDirty(false)
        return true
      } catch (error) {
        if (!options?.silent) {
          const message = parseApiErrorMessage(error)
          toast.error(message ?? 'Unable to save club selection.')
        }
        return false
      } finally {
        setClubSavePending(false)
      }
    },
    [
      applyShowEntrantUpdate,
      clubsEnabled,
      showId,
      entrantClubSelection.clubId,
      entrantClubSelection.clubName,
      isOnline,
      parseApiErrorMessage,
      updateShowExhibitor,
      clubSelectionDirty,
    ]
  )

  const clubContinuePending = clubSavePending || updateShowExhibitor.isPending

  const handleClubStepContinue = useCallback(async () => {
    return persistClubSelection()
  }, [persistClubSelection])

  const handleClearSelectedExhibitor = () => {
    setSelectedExhibitor(null)
    setHighlightEntryId(null)
    setPendingStepId(undefined)
    setClubStepEnabled(false)
    setEntrantGrandChampionEligible(true)
    applyExhibitorClubDefaults(null)
  }

  const handleFinalizeEntrant = useCallback(
    async (exhibitor: TExhibitorWithEntries) => {
      const targetShowId = showId

      if (updateShowEntrantFinalization.isPending) {
        return exhibitor ?? null
      }

      if (!targetShowId) {
        toast.error('Select a show before finalizing entries.')
        return null
      }

      if (!exhibitor || !exhibitor.id) {
        toast.error('Select an entrant before finalizing entries.')
        return null
      }

      const entries = Array.isArray(exhibitor.entries)
        ? (exhibitor.entries.filter(Boolean) as ExhibitorEntrySummary[])
        : []

      if (entries.length === 0 && !exhibitor.finalizedAt) {
        toast.error('Add at least one entry before finalizing entries.')
        return null
      }

      if (exhibitor.finalizedAt) {
        return exhibitor
      }

      try {
        const showEntrant = await updateShowEntrantFinalization.mutateAsync({
          showId: targetShowId,
          exhibitorId: exhibitor.id,
          payload: { finalized: true },
        })

        const nextEntrant: TExhibitorWithEntries = {
          ...exhibitor,
          finalizedAt: showEntrant.finalizedAt ?? null,
          finalizedBy: showEntrant.finalizedBy ?? null,
          finalizedByRole: showEntrant.finalizedByRole ?? null,
        }

        setSelectedExhibitor(previous => {
          if (!previous || previous.id !== exhibitor.id) return previous
          return nextEntrant
        })

        // A response that lands after staff moved on must not reselect this entrant.
        const stillSelected = selectedExhibitorRef.current?.id === exhibitor.id
        if (stillSelected) {
          selectedExhibitorRef.current = nextEntrant
        }

        if (queryClient) {
          void queryClient.invalidateQueries({
            predicate: query => {
              const key = query.queryKey
              if (!Array.isArray(key) || key[0] !== 'exhibitors') return false
              const params = key[1]
              if (!params || typeof params !== 'object') return false
              return (params as { showId?: string | null }).showId === showId
            },
          })
        }

        toast.success('Entries finalized')
        return stillSelected ? nextEntrant : null
      } catch (error) {
        const message = parseApiErrorMessage(error)
        toast.error(message || 'Unable to finalize entries')
        return null
      }
    },
    [parseApiErrorMessage, queryClient, showId, updateShowEntrantFinalization]
  )

  const autoSyncEntries = useMemo(
    () =>
      currentShow.status === 'accepting_entries'
        ? intake.pendingEntries.filter(entry => !entry.syncAttempt?.completed)
        : intake.pendingEntries.filter(entry => entry.syncAttempt && !entry.syncAttempt.completed),
    [currentShow.status, intake.pendingEntries]
  )
  const autoSync = useCallback(
    () => syncPendingEntries(autoSyncEntries, { isBackground: true }),
    [autoSyncEntries, syncPendingEntries]
  )

  usePendingEntryAutoSync({
    pendingEntries: autoSyncEntries,
    isOnline,
    enabled: Boolean(
      showId &&
      intake.initialized &&
      !entryPermissionDisabled &&
      (currentShow.status === 'accepting_entries' || autoSyncEntries.length > 0)
    ),
    sync: autoSync,
  })

  const { checkedInExhibits } = intake
  const recordCheckedInExhibit = intake.recordCheckedInExhibit
  const [checkingInEntryId, setCheckingInEntryId] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedExhibitor?.entries || selectedExhibitor.entries.length === 0) return
    const knownEntryIds = new Set(checkedInExhibits.map(item => item.entryId))
    const knownExhibitIds = new Set(checkedInExhibits.map(item => item.exhibitId))

    for (const entry of selectedExhibitor.entries) {
      if (!entry.exhibitId) continue
      if (knownEntryIds.has(entry.id) || knownExhibitIds.has(entry.exhibitId)) continue
      void Promise.resolve(
        recordCheckedInExhibit({
          exhibitId: entry.exhibitId,
          entryId: entry.id,
          entryCode: entry.entryCode,
          classId: entry.class?.id ?? null,
          classCode: entry.class?.code ?? null,
          className: entry.class?.name ?? null,
          exhibitorId: selectedExhibitor.id ?? null,
          exhibitorName: selectedExhibitor.name ?? selectedExhibitor.person?.name ?? null,
        })
      ).catch(() => toast.error('Unable to save recent check-ins on this device.'))
    }
  }, [selectedExhibitor?.entries, checkedInExhibits, recordCheckedInExhibit])

  const handleManualCheckIn = useCallback(
    async (entry: ExhibitorEntrySummary): Promise<CheckedInExhibit | null> => {
      if (!showId) {
        toast.error('Select a show before checking in entries.')
        return null
      }
      if (entryPermissionDisabled) {
        toast.error(entryPermissionReason ?? 'Checking in entries requires additional permissions.')
        return null
      }
      if (!isOnline) {
        toast.error('Go online before checking in entries.')
        return null
      }
      if (!entry.id) {
        toast.error('Unable to check in entry without an Exhibit ID.')
        return null
      }
      if (!entry.entryCode || entry.entryCode.startsWith('TMP-')) {
        toast.error('Check-in requires a canonical Exhibit ID.')
        return null
      }
      if (currentShow.status !== 'accepting_entries') {
        toast.error('Check-in is available when the show is accepting entries.')
        return null
      }
      if (checkInEntry.isPending && checkingInEntryId && checkingInEntryId !== entry.id) {
        toast.info('Finishing another check-in. Try again in a moment.')
        return null
      }
      if (checkInEntry.isPending && checkingInEntryId === entry.id) {
        return null
      }

      const activeExhibitor = selectedExhibitorRef.current
      const exhibitorId = activeExhibitor?.id
      try {
        setCheckingInEntryId(entry.id)
        if (exhibitorId && !(await verifyEntrantEntryEligibility(exhibitorId))) return null
        const exhibit = await checkInEntry.mutateAsync({ entryId: entry.id, payload: {} })
        if (!exhibit?.id) {
          throw new Error('Check-in response missing exhibit details.')
        }

        if (exhibitorId) {
          await applyEntryUpdate(exhibitorId, {
            ...entry,
            status: 'checked_in',
            exhibitId: exhibit.id,
          })
        }

        const canonicalEntryCode = entry.entryCode ?? ''
        const summary: CheckedInExhibit = {
          exhibitId: exhibit.id,
          entryId: entry.id,
          entryCode: canonicalEntryCode,
          classId: entry.class?.id ?? null,
          classCode: entry.class?.code ?? null,
          className: entry.class?.name ?? null,
          exhibitorId: activeExhibitor?.id ?? null,
          exhibitorName: activeExhibitor?.name ?? activeExhibitor?.person?.name ?? null,
          checkedInAt: new Date().toISOString(),
        }
        await recordCheckedInExhibit(summary)

        toast.success(canonicalEntryCode ? `Checked in ${canonicalEntryCode}` : 'Entry checked in')
        return summary
      } catch (error) {
        toast.error(parseApiErrorMessage(error) || 'Unable to check in entry')
        return null
      } finally {
        setCheckingInEntryId(null)
      }
    },
    [
      checkInEntry,
      applyEntryUpdate,
      checkingInEntryId,
      showId,
      currentShow.status,
      entryPermissionDisabled,
      entryPermissionReason,
      isOnline,
      parseApiErrorMessage,
      recordCheckedInExhibit,
      verifyEntrantEntryEligibility,
    ]
  )

  const attemptAutoPrint = useCallback(
    async (entry: ExhibitorEntrySummary, checkedIn: CheckedInExhibit) => {
      if (!autoPrintReady) return
      if (!entry.entryCode || entry.entryCode.startsWith('TMP-')) return
      if (!checkedIn.exhibitId) return

      setAutoPrintError(null)
      setAutoPrintInFlight(true)
      setAutoPrintEntryCode(entry.entryCode ?? checkedIn.entryCode ?? null)

      const classCodeFromLookup =
        entry.class?.id && classLookup.has(entry.class.id)
          ? (classLookup.get(entry.class.id)?.code ?? null)
          : null

      const payload = {
        entryCode: entry.entryCode,
        classCode: checkedIn.classCode ?? entry.class?.code ?? classCodeFromLookup ?? null,
        showName: currentShow.name ?? '',
      }

      const copies = Math.max(1, getLabelsPerEntry(entry))
      const jobKey =
        entry.id ?? checkedIn.exhibitId ?? entry.entryCode ?? checkedIn.entryId ?? 'label-job'

      try {
        await sendToPrinter(payload, { copies, jobKey })
        await markLabelPrinted.mutateAsync({ exhibitId: checkedIn.exhibitId })
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to print label automatically.'
        setAutoPrintError({
          entry,
          checkedIn,
          message,
        })
      } finally {
        setAutoPrintEntryCode(null)
        setAutoPrintInFlight(false)
      }
    },
    [
      autoPrintReady,
      classLookup,
      currentShow.name,
      getLabelsPerEntry,
      markLabelPrinted,
      sendToPrinter,
    ]
  )

  const handleRetryAutoPrint = useCallback(() => {
    if (!autoPrintError) return
    const context = autoPrintError
    setAutoPrintError(null)
    void attemptAutoPrint(context.entry, context.checkedIn)
  }, [attemptAutoPrint, autoPrintError])

  const completeEntryCreation = useCallback(
    async ({
      entry,
      usedCode,
      classId,
      divisionId,
      klass,
      checkedIn,
    }: {
      entry: TEntry
      usedCode: string
      classId: string
      divisionId: string
      klass?: DivisionOption['classes'][number]
      checkedIn?: CheckedInExhibit
    }) => {
      setDuplicateConflict(null)
      await handleEntrySuccess(entry, usedCode, undefined, {
        className: klass?.name ?? null,
        classId,
        classCode: typeof klass?.code === 'string' ? klass.code : undefined,
        checkedIn,
      })
      // A plain reset drops the field refs, which would turn setFocus into a no-op.
      entryForm.reset(
        {
          divisionId,
          classId,
          notes: '',
        },
        { keepFieldsRef: true }
      )
      entryForm.setFocus('classId')

      if (!checkedIn) return

      if (!entry.id) {
        toast.error('Unable to check in entry automatically. Try checking in manually.')
        return
      }

      let classSummary: ExhibitorEntrySummary['class'] = null
      if (klass) {
        const classIdValue = klass.id ?? entry.classId
        if (classIdValue) {
          classSummary = {
            id: classIdValue,
            code: typeof klass.code === 'string' ? klass.code : classIdValue,
            name: klass.name ?? 'Selected class',
          }
        }
      }

      const summaryForCheckIn: ExhibitorEntrySummary = {
        id: entry.id,
        entryCode: entry.entryCode ?? usedCode,
        status: entry.status ?? 'draft',
        class: classSummary,
      }

      toast.success(`Checked in ${checkedIn.entryCode}`)
      void attemptAutoPrint(summaryForCheckIn, checkedIn)
    },
    [attemptAutoPrint, entryForm, handleEntrySuccess, setDuplicateConflict]
  )

  useEffect(() => {
    if (isOnline && intake.initialized && selectedExhibitor?.id && !entryPermissionDisabled) {
      void intake.refreshAssignmentContext(selectedExhibitor.id).catch(() => undefined)
    }
  }, [
    isOnline,
    intake.initialized,
    intake.refreshAssignmentContext,
    selectedExhibitor?.id,
    entryPermissionDisabled,
  ])

  const handleEntrySubmit = async (values: EntryFormValues, event?: BaseSyntheticEvent) => {
    const nativeEvent = event?.nativeEvent as SubmitEvent | undefined
    const submitter = nativeEvent?.submitter as HTMLButtonElement | null | undefined
    const shouldCheckIn = submitter?.dataset?.action !== 'create-only'

    setAutoPrintError(null)
    setAutoPrintEntryCode(null)

    if (!showId) {
      toast.error('Select a show before creating entries.')
      return
    }
    if (!selectedExhibitor) {
      toast.error('Choose or create an exhibitor before adding entries.')
      return
    }
    if (!selectedExhibitor.id) {
      toast.error('Selected exhibitor has no ID. Please select a different exhibitor.')
      return
    }
    const exhibitorId = selectedExhibitor.id
    const exhibitorName = selectedExhibitor.name ?? 'Unknown exhibitor'
    if (entryPermissionDisabled) {
      toast.error(entryPermissionReason ?? 'Creating entries requires additional permissions.')
      return
    }

    if (currentShow.status !== 'accepting_entries') {
      toast.error('This show is no longer accepting entries. Intake actions are disabled.')
      return
    }

    const classId = values.classId
    const divisionId = values.divisionId
    if (!classId || !divisionId || !hasDivisions || !hasClasses) {
      toast.error('Add at least one division and class before creating entries.')
      return
    }
    if (enteredClasses.has(classId)) {
      toast.error('This entrant already has an active entry in this class.')
      return
    }
    if (!(await verifyEntrantEntryEligibility(exhibitorId))) return

    const selectedClubId = clubsEnabled ? (entrantClubSelection.clubId ?? null) : null
    const normalizedClubId = clubsEnabled ? (selectedClubId ?? null) : null
    const rawClubName = clubsEnabled ? (entrantClubSelection.clubName ?? '') : ''
    const normalizedClubName = clubsEnabled ? rawClubName.trim() : ''

    if (clubsEnabled) {
      const saved = await persistClubSelection()
      if (!saved) {
        return
      }
    }

    const klass = classOptions.find(item => item.id === classId)

    const submissionId = crypto.randomUUID()
    if (isOnline && intake.availableCodes.length === 0) {
      await allocateAndStoreCodes(showId, 10, { silent: true })
    }
    // A restricted show refreshed this history while verifying eligibility above.
    if (isOnline && staffMayExhibit)
      await refreshAssignmentContextBriefly(exhibitorId).catch(() => undefined)
    let pending: PendingIntakeEntry
    try {
      pending = await intake.claimAndAddPending(
        {
          id: submissionId,
          submissionId,
          completionIntent: shouldCheckIn ? 'create_and_check_in' : 'create_only',
          exhibitor: { id: exhibitorId, name: exhibitorName },
          classId,
          className: klass?.name ?? 'Unknown class',
          notes: normalizeOptional(values.notes),
          status: 'queued',
          clubId: clubsEnabled ? normalizedClubId : null,
          clubName: clubsEnabled ? normalizedClubName || null : null,
        },
        { staffMayExhibit }
      )
    } catch (error) {
      toast.error(
        error instanceof EntryEligibilityError
          ? error.message
          : 'Unable to save this entry on the device. No entry was sent. Try again after checking browser storage.'
      )
      return
    }
    const claimed = {
      code: pending.code,
      source: pending.code.startsWith('TMP') ? 'temporary' : 'canonical',
    }
    if (!isOnline) return

    try {
      const result = await syncPendingEntry({
        intake,
        id: pending.id,
        showId,
        showStatus: currentShow.status,
        create: input => createEntry.mutateAsync(input),
        checkIn: entryId => checkInEntry.mutateAsync({ entryId, payload: {} }),
        read: entryId => apiClient.getEntry(entryId),
      })
      if (!result) return
      if (result === 'deferred') {
        toast.info(PENDING_SYNC_DEFERRED_MESSAGE)
        return
      }
      await completeEntryCreation({
        entry: result.entry,
        usedCode: claimed.code,
        classId,
        divisionId,
        klass,
        checkedIn: result.checkedIn,
      })
    } catch (error) {
      if (isOfflineError(error)) {
        return
      }

      const payload = parseApiErrorPayload(error)
      if (payload?.status === 'duplicate_entry') {
        const message = payload.message ?? 'This entrant already has an active entry in this class.'
        setDuplicateConflict({
          submissionId: pending.submissionId,
          className: klass?.name ?? null,
          message,
        })
        toast.error(message)
        return
      }

      const message =
        payload?.message ?? (error instanceof Error ? error.message : 'Unable to create entry')
      toast.error(message)
      if (claimed.source === 'canonical') {
        toast.info('The entry is retained in Pending entries for review. Keep any issued label.')
      }
    }
  }

  const handleDismissDuplicate = async () => {
    if (duplicateConflict?.submissionId) await intake.removePending(duplicateConflict.submissionId)
    setDuplicateConflict(null)
  }

  const selectedEntriesListProps = useMemo<SelectedExhibitorEntriesListProps | null>(() => {
    if (!selectedExhibitor) return null
    const entries = Array.isArray(selectedExhibitor.entries)
      ? (selectedExhibitor.entries.filter(Boolean) as ExhibitorEntrySummary[])
      : []
    const uniqueEntries = new Map<string, ExhibitorEntrySummary>()
    for (const entry of entries) {
      if (entry.id && !uniqueEntries.has(entry.id)) {
        uniqueEntries.set(entry.id, entry)
      }
    }

    return {
      labelPrintShow: currentShow,
      entries: Array.from(uniqueEntries.values()),
      checkedInExhibits,
      exhibitorId: selectedExhibitor.id ?? null,
      exhibitorName: selectedExhibitor.name ?? null,
      exhibitorPersonName: selectedExhibitor.person?.name ?? null,
      currentShowStatus: currentShow.status ?? null,
      isOnline,
      entryPermissionDisabled,
      entryPermissionReason,
      checkInRestriction: selectedEntryRestriction,
      onManualCheckIn: handleManualCheckIn,
      checkingInEntryId,
      checkInPending: checkInEntry.isPending || Boolean(checkingInEntryId),
      hideEntryCodes,
      highlightEntryId,
      onDeleteEntry: handleDeleteEntry,
      deleteEntryPendingId,
      canDeleteEntries,
      deleteDisabledReason,
    }
  }, [
    checkedInExhibits,
    checkInEntry.isPending,
    checkingInEntryId,
    currentShow,
    entryPermissionDisabled,
    entryPermissionReason,
    selectedEntryRestriction,
    handleManualCheckIn,
    hideEntryCodes,
    highlightEntryId,
    isOnline,
    selectedExhibitor,
    deleteEntryPendingId,
    handleDeleteEntry,
    canDeleteEntries,
    deleteDisabledReason,
  ])

  const currentShowId = currentShow.id ?? null
  const showAllowsCheckIn = currentShow.status === 'accepting_entries'
  const recentCheckIns = useMemo(() => {
    if (!showAllowsCheckIn || checkedInExhibits.length === 0) return null
    return (
      <RecentCheckIns
        events={checkedInExhibits}
        showId={currentShowId}
        listIdPrefix="checkin-recent-checkins"
        showSyncNowPrompt={false}
        onSyncPending={() => {}}
        syncDisabled={false}
        disableOpenEntry
      />
    )
  }, [checkedInExhibits, currentShowId, showAllowsCheckIn])

  const entryAutoPrintError = autoPrintError
    ? {
        entryCode: autoPrintError.entry.entryCode ?? autoPrintError.checkedIn.entryCode ?? null,
        message: autoPrintError.message,
      }
    : null

  const showStatusLabel = currentShow.status
    ? currentShow.status.replace(/_/g, ' ')
    : 'unknown status'
  const finalizeStepDescription = selectedExhibitor
    ? `Review entries for ${selectedExhibitor.name ?? 'this entrant'} before completing check-in.`
    : 'Select an entrant and review their entries before completing check-in.'

  const selectedEntriesCount = selectedEntriesListProps?.entries?.length ?? 0
  const finalizeMutationPending = updateShowEntrantFinalization.isPending
  const requireEntryBeforeFinalizing = selectedEntriesCount === 0 && !selectedExhibitor?.finalizedAt
  const finalizeEntriesDisabled =
    createEntry.isPending ||
    checkInEntry.isPending ||
    finalizeMutationPending ||
    requireEntryBeforeFinalizing
  const finalizeEntriesTooltip = createEntry.isPending
    ? 'Entry submission in progress'
    : checkInEntry.isPending
      ? 'Entry check-in in progress'
      : finalizeMutationPending
        ? 'Finalizing entrant…'
        : requireEntryBeforeFinalizing
          ? 'Add at least one entry before finalizing.'
          : undefined

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <PageHeading>
          {selectedExhibitor ? (
            <span>
              Check-In{' '}
              <span className="text-muted-foreground">
                &gt; {selectedExhibitor.name ?? selectedExhibitor.person?.name ?? 'Entrant'}
              </span>
            </span>
          ) : (
            'Check-In'
          )}
        </PageHeading>
        {allowsLabelPrinting ? (
          <LabelPrinterToolbar state={labelPrinter} className="self-start" />
        ) : null}
      </header>

      {labelPrintingDisabled && <LabelPrintingDisabledNotice />}

      {!isOnline && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Offline mode enabled</p>
          <p className="mt-1">
            New entries are stored locally until you reconnect. Sync and code fetch actions are
            disabled while offline.
          </p>
        </div>
      )}

      {duplicateConflict && (
        <div
          role="alert"
          className="rounded-md border border-amber-400 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold">Already entered</p>
              <p>{duplicateConflict.message}</p>
              <p className="text-xs text-amber-900/80">
                {selectedExhibitor?.name ?? 'This entrant'} can have only one active entry in{' '}
                {duplicateConflict.className ?? 'this class'}. The new entry was not saved.
              </p>
            </div>
            <div className="flex flex-wrap items-start gap-2">
              <Button size="sm" variant="outline" onClick={handleDismissDuplicate}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {mode === 'run' && currentShow.status !== 'accepting_entries' && currentShow && (
        <Card className="border-dashed border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle>
              Show is in <span className="capitalize">{showStatusLabel}</span>
            </CardTitle>
            <CardDescription>
              Entry intake actions are limited while the show is in this phase. Review class rosters
              below or switch status when intake resumes.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/shows">Review shows</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${currentShow.slug}/setup`}>Open setup guide</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {currentShow.awardsPolicy?.heroAwardEnabled && selectedExhibitor?.id && (
        <div className="rounded-md border bg-card p-4 text-card-foreground">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={selectedExhibitor.heroAwardEligible === true}
              disabled={!isOnline || !canManageExhibitors || updateShowExhibitor.isPending}
              onChange={async event => {
                try {
                  const saved = await updateShowExhibitor.mutateAsync({
                    showId: currentShow.id!,
                    exhibitorId: selectedExhibitor.id!,
                    payload: { heroAwardEligible: event.target.checked },
                  })
                  applyShowEntrantUpdate(selectedExhibitor.id!, saved)
                } catch (error) {
                  toast.error(parseApiErrorMessage(error))
                }
              }}
            />
            <span>Hero Award eligible — veteran or first responder</span>
          </label>
          {!isOnline && (
            <p className="text-sm text-muted-foreground">Connect to confirm eligibility.</p>
          )}
        </div>
      )}
      <CheckInStepper
        initialEntrant={selectedExhibitor}
        initialStepId={pendingStepId}
        onStepChange={stepId => {
          if (pendingStepIdRef.current && stepId === pendingStepIdRef.current) {
            setPendingStepId(undefined)
          }
          const previousStepId = lastStepIdRef.current
          if (
            clubsEnabled &&
            clubSelectionDirty &&
            previousStepId === 'club' &&
            stepId !== 'club'
          ) {
            void persistClubSelection({ silent: true })
          }
          lastStepIdRef.current = stepId
        }}
        onEntrantChange={entrant => {
          if (entrant) {
            handleSelectExhibitor(entrant)
          } else {
            handleClearSelectedExhibitor()
          }
        }}
        steps={[
          {
            id: 'entrant',
            label: 'Entrant Details',
            content: (
              <EntrantStep
                currentShowId={currentShow.id ?? null}
                currentShowOrgId={currentShow.orgId ?? null}
                permissionLoading={permissionLoading}
                canManageExhibitors={canManageExhibitors}
                exhibitorRolesLabel={exhibitorRolesLabel}
                exhibitorPermissionDisabled={exhibitorPermissionDisabled}
                exhibitorPermissionReason={exhibitorPermissionReason}
                mergePermissionDisabled={mergePermissionDisabled}
                mergePermissionReason={mergePermissionReason}
                exhibitors={exhibitorsQuery.data ?? []}
                exhibitorsByPersonId={exhibitorsByPersonId}
                exhibitorsLoading={exhibitorsQuery.isLoading}
                onOpenMergePerson={handleOpenMergePerson}
                onEntrantReady={handleEntrantReadyStep}
                isOnline={isOnline}
                clubsEnabled={clubsEnabled}
                grandChampionEligible={entrantGrandChampionEligible}
                onGrandChampionEligibilityChange={handleGrandChampionEligibilityChange}
                disableGrandChampionToggle={grandChampionToggleDisabled}
                grandChampionToggleTitle={grandChampionToggleTitle}
              />
            ),
          },
          clubsEnabled && (clubStepEnabled || pendingStepId === 'club')
            ? {
                id: 'club',
                label: 'Club',
                content: (
                  <ClubStep
                    clubSelection={entrantClubSelection}
                    onClubSelectionChange={handleClubSelectionChange}
                    isOnline={isOnline}
                    showHasOrganization={Boolean(currentShow.orgId)}
                    showOrganizationId={currentShow.orgId ?? null}
                    grandChampionEligible={entrantGrandChampionEligible}
                    onGrandChampionEligibilityChange={handleGrandChampionEligibilityChange}
                    disableGrandChampionToggle={grandChampionToggleDisabled}
                    grandChampionToggleTitle={grandChampionToggleTitle}
                    onContinue={handleClubStepContinue}
                    continuePending={clubContinuePending}
                  />
                ),
              }
            : null,
          {
            id: 'entries',
            label: 'Entries',
            renderFooter: () => null,
            content: (
              <EntriesStep
                exhibitor={selectedExhibitor}
                entriesListProps={selectedEntriesListProps}
                finalizeDisabled={finalizeEntriesDisabled}
                finalizeTooltip={finalizeEntriesTooltip}
                finalizePending={finalizeMutationPending}
                onFinalizeEntrant={handleFinalizeEntrant}
                backStepId="entrant"
                finalizeStepId="finalize"
                entryFormCard={
                  <EntryFormCard
                    form={entryForm}
                    divisionOptions={divisionOptions}
                    classOptions={classOptions}
                    enteredClasses={enteredClasses}
                    fullyEnteredDivisionIds={fullyEnteredDivisionIds}
                    hasDivisions={hasDivisions}
                    hasClasses={hasClasses}
                    primarySubmitLabel={primarySubmitLabel}
                    onSubmit={handleEntrySubmit}
                    entryButtonDisabled={entryButtonDisabled}
                    entryButtonTooltip={entryButtonTooltip}
                    permissionLoading={permissionLoading}
                    canManageEntries={canManageEntries}
                    entriesRolesLabel={entriesRolesLabel}
                    isOnline={isOnline}
                    entryActionMessage={entryActionMessage}
                    selectedExhibitorPresent={Boolean(selectedExhibitor)}
                    autoPrintStatusMessage={entryAutoPrintStatusMessage}
                    autoPrintInFlight={autoPrintInFlight}
                    autoPrintEntryCode={autoPrintEntryCode}
                    autoPrintError={entryAutoPrintError}
                    onRetryAutoPrint={handleRetryAutoPrint}
                    onDismissAutoPrintError={() => setAutoPrintError(null)}
                  />
                }
                recentCheckIns={recentCheckIns}
              />
            ),
          },
          {
            id: 'finalize',
            label: 'Finalize Entries',
            renderFooter: () => null,
            content: (
              <FinalizeStep
                labelPrintShow={currentShow}
                description={finalizeStepDescription}
                showId={currentShow.id ?? null}
                showName={currentShow.name ?? null}
                isOnline={isOnline}
                packetPermissionDisabled={packetPermissionDisabled}
                packetPermissionReason={packetPermissionReason}
                printerSetupMessage={autoPrintStatusMessage}
                labelPrintingDisabled={labelPrintingDisabled}
              />
            ),
          },
        ]}
      />

      <MergePeopleSheet
        open={mergeOpen}
        onOpenChange={handleMergeOpenChange}
        targetPerson={mergeTarget}
      />
    </div>
  )
}
